import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CustomButton from "../../pages/CustomButton";
import { BASE_URL } from "../../config";
import { getAuthHeaders } from "../../services/useAuth";
import EmployeeMapTracker from "../../pages/EmployeeMapTracker";
import formatActivityType from "../ActivityEnum";
import * as XLSX from "xlsx";

function LocationHistoryTime() {
  const location = useLocation();
  const navigate = useNavigate();
  const printableRef = useRef(null);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [mapView, setMapView] = useState(null);
  const [employeeInfo, setEmployeeInfo] = useState(null);
  const [error, setError] = useState(null);

  const getQueryParams = () => {
    const params = new URLSearchParams(location.search);
    const qEmployeeId =
      params.get("employee_id") || params.get("employeeId") || null;
    const qTrackDate =
      params.get("track_date") ||
      params.get("trackDate") ||
      params.get("working_date") ||
      null;
    const employeeId =
      qEmployeeId ||
      sessionStorage.getItem("location_history_employee_id") ||
      null;
    const rawTrackDate =
      qTrackDate ||
      sessionStorage.getItem("location_history_track_date") ||
      null;
    return {
      employeeId,
      rawTrackDate,
      lat: params.get("lat") || null,
      lng: params.get("lng") || null,
      time: params.get("time") || null,
      client: params.get("client") || null,
      retailerId: params.get("retailer_id") || params.get("retailerId") || null,
    };
  };

  const formatDateForAPI = (dateStr) => {
    if (!dateStr) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    const parsed = new Date(dateStr);
    if (!isNaN(parsed.getTime())) {
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, "0");
      const d = String(parsed.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
    const match = dateStr.match(/(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
    return null;
  };
  useEffect(() => {
    const params = getQueryParams();
    if (!params.employeeId) {
      setError(
        "Missing employee_id in query params. Provide employee_id or employeeId in URL.",
      );
      setData([]);
      setLoading(false);
      return;
    }
    if (!params.rawTrackDate) {
      setError(
        "Missing track_date in query params. Provide track_date or trackDate in URL.",
      );
      setData([]);
      setLoading(false);
      return;
    }
    const trackDate = formatDateForAPI(params.rawTrackDate);
    if (!trackDate) {
      setError(
        "track_date could not be parsed. Use a recognizable date like 2025-11-06 or 06 Nov 2025.",
      );
      setData([]);
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      setError(null);
      setLoading(true);
      const url = `${BASE_URL}/api/employee/${encodeURIComponent(
        params.employeeId,
      )}/location-report-tp?track_date=${encodeURIComponent(trackDate)}`;
      try {
        const res = await fetch(url, { headers: getAuthHeaders() });
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`API Error ${res.status} ${txt}`);
        }
        const result = await res.json();
        const locations = Array.isArray(result.locations)
          ? result.locations
          : [];
        setEmployeeInfo(result.employee || null);
        const transformed = locations.map((loc, i) => ({
          sNo: i + 1,
          locationId: loc.location_id,
          deviceId: loc.device_id,
          mobile: `${loc.mobile_name || ""} Android ${
            loc.android_version || ""
          }`.trim(),
          mobileTime: loc.mobile_time
            ? new Date(loc.mobile_time).toLocaleString("en-US", {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })
            : "—",
          serverTime: loc.server_time
            ? new Date(loc.server_time).toLocaleString("en-US", {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })
            : "—",
          ip: loc.ip_address || "—",
          latitude:
            typeof loc.latitude === "number"
              ? loc.latitude.toFixed(6)
              : loc.latitude || "0.000000",
          longitude:
            typeof loc.longitude === "number"
              ? loc.longitude.toFixed(6)
              : loc.longitude || "0.000000",
          distance:
            typeof loc.distance_km === "number"
              ? loc.distance_km.toFixed(2)
              : loc.distance_km || "0.00",
          kmCovered:
            typeof loc.km_covered === "number"
              ? loc.km_covered.toFixed(2)
              : loc.km_covered || "0.00",
          address: loc.address || loc.employee_address || "—",
          accuracy:
            typeof loc.accuracy_meter === "number"
              ? Math.round(loc.accuracy_meter)
              : "—",
          dataMock: loc.is_mock === true ? "True" : "False",
          type: loc.os_type || "—",
          provider: loc.location_provider || "—",
          speed:
            typeof loc.speed === "number"
              ? loc.speed.toFixed(1)
              : loc.speed || "0.0",
          employeeId: loc.employee_id || params.employeeId,
          _rawMobileTime: loc.mobile_time || null,
          _rawServerTime: loc.server_time || null,
        }));
        setData(transformed);
      } catch (err) {
        setError(err.message || String(err));
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [location.search]);

  const handleDelete = async (locationId) => {
    try {
      const res = await fetch(
        `${BASE_URL}/api/employee/location/${locationId}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        },
      );
      if (res.ok) {
        setData((prev) => prev.filter((r) => r.locationId !== locationId));
        setSelectedRows((prev) => {
          const s = new Set(prev);
          s.delete(locationId);
          return s;
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.size === 0) return;
    try {
      const promises = Array.from(selectedRows).map((id) =>
        fetch(`${BASE_URL}/api/employee/location/${id}`, {
          method: "DELETE",
          headers: getAuthHeaders(),
        }),
      );
      await Promise.all(promises);
      setData((prev) => prev.filter((r) => !selectedRows.has(r.locationId)));
      setSelectedRows(new Set());
    } catch (e) {
      console.error(e);
    }
  };

  const handleSelectRow = (locationId) => {
    setSelectedRows((prev) => {
      const s = new Set(prev);
      if (s.has(locationId)) s.delete(locationId);
      else s.add(locationId);
      return s;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked)
      setSelectedRows(new Set(data.map((d) => d.locationId)));
    else setSelectedRows(new Set());
  };

  const handleViewMap = (row) => {
    const params = getQueryParams();
    const rawTrackDate =
      params.rawTrackDate || row._rawMobileTime || row._rawServerTime || null;
    const trackDate =
      formatDateForAPI(rawTrackDate) ||
      formatDateForAPI(params.rawTrackDate) ||
      null;

    const locId = row.locationId || row.location_id;
    const empId =
      row.employeeId ||
      row.employee_id ||
      params.employeeId ||
      sessionStorage.getItem("location_history_employee_id") ||
      "";

    setMapView({
      latitude: row.latitude
        ? parseFloat(String(row.latitude).replace(/,/g, ""))
        : undefined,
      longitude: row.longitude
        ? parseFloat(String(row.longitude).replace(/,/g, ""))
        : undefined,
      employeeId: String(empId || ""),
      trackDate: trackDate || formatDateForAPI(params.rawTrackDate) || null,
      locationId: locId || null,
      employeeName:
        (employeeInfo && employeeInfo.name) || row.employeeName || "",
    });
  };

  const getRowColor = (row) => {
    if (selectedRows.has(row.locationId))
      return "bg-orange-100 hover:bg-orange-200";
    const distance = parseFloat(row.distance);
    if (isNaN(distance)) return "bg-gray-50";
    if (distance === 0) return "bg-red-50 hover:bg-red-100";
    if (distance > 100) return "bg-purple-50 hover:bg-purple-100";
    return "bg-green-50 hover:bg-green-100";
  };

  // Print (same approach as earlier)
  const handlePrint = () => {
    const node = printableRef.current;
    if (!node) {
      alert("Nothing to print");
      return;
    }

    const w = window.open("", "_blank", "width=1000,height=800");
    if (!w) return;

    const style = `
      @media print { @page { size: landscape; margin: 12mm } }
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial; color: #111; padding: 18px; }
      table { border-collapse: collapse; width: 100%; font-size: 12px; }
      th, td { border: 1px solid #e5e7eb; padding: 8px; text-align: left; }
      th { background: #f3f4f6; font-weight: 600; }
      .print-header { text-align: center; margin-bottom: 12px; }
      .print-header h2 { margin: 0 0 6px 0; font-size: 18px; }
      .print-header p { margin: 0; color: #4b5563; font-size: 13px; }
    `;

    w.document.open();
    w.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>Location History Print</title><style>${style}</style></head><body>`,
    );

    const empName = employeeInfo?.name || "";
    const empHQ = employeeInfo?.headquarter || "";
    const params = getQueryParams();
    const trackDate =
      formatDateForAPI(params.rawTrackDate) || params.rawTrackDate || "";

    w.document.write(
      `<div class="print-header"><h2>Location History${
        empName ? ` - ${empName}` : ""
      }</h2><p>Headquarter: ${empHQ || "N/A"} | Date: ${
        trackDate || "N/A"
      }</p></div>`,
    );

    const clone = node.cloneNode(true);
    clone.querySelectorAll("button, input, a").forEach((el) => el.remove());
    clone.querySelectorAll(".overflow-x-auto, .table-wrapper").forEach((el) => {
      el.style.overflow = "visible";
    });

    w.document.body.appendChild(clone);
    w.document.write("</body></html>");
    w.document.close();
    w.focus();
    setTimeout(() => {
      w.print();
    }, 400);
  };
  const handleExportExcel = () => {
    if (!printableRef.current) {
      alert("No table found to export");
      return;
    }
    const tableEl = printableRef.current.querySelector("table");
    if (!tableEl) {
      alert("Table element not found inside printable area");
      return;
    }

    try {
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.table_to_sheet(tableEl, { raw: true });
      const range = ws["!ref"] ? XLSX.utils.decode_range(ws["!ref"]) : null;
      if (range) {
        for (let R = range.s.r; R <= range.e.r; ++R) {
          for (let C = range.s.c; C <= range.e.c; ++C) {
            const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
            const cell = ws[cellRef];
            if (!cell || cell.t === "n" || cell.t === "d") continue;
            if (R === range.s.r) continue;
            const v = cell.v;
            if (typeof v === "string") {
              const trimmed = v.trim();
              const numberLike =
                /^-?\d{1,3}(?:,\d{3})*(?:\.\d+)?$|^-?\d+(?:\.\d+)?$/.test(
                  trimmed,
                );
              if (numberLike) {
                const parsed = Number(trimmed.replace(/,/g, ""));
                if (!isNaN(parsed)) {
                  ws[cellRef] = { v: parsed, t: "n" };
                  continue;
                }
              }
              const maybeDate = new Date(trimmed);
              if (!isNaN(maybeDate.getTime())) {
                ws[cellRef] = { v: maybeDate, t: "d" };
                continue;
              }
            }
          }
        }
      }

      // add simple column widths
      // heuristics: map table columns to widths based on header text length
      const firstRow = [];
      if (range) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const hdrCell = ws[XLSX.utils.encode_cell({ r: range.s.r, c: C })];
          const hdr = hdrCell ? String(hdrCell.v) : "";
          firstRow.push({ wch: Math.min(40, Math.max(10, hdr.length + 8)) });
        }
        ws["!cols"] = firstRow;
      }

      XLSX.utils.book_append_sheet(wb, ws, "Location History");

      const params = getQueryParams();
      const empNameSafe = (
        employeeInfo?.name ||
        params.employeeId ||
        "employee"
      )
        .replace(/\s+/g, "_")
        .replace(/[^\w\-]/g, "");
      const trackDate =
        formatDateForAPI(params.rawTrackDate) ||
        params.rawTrackDate ||
        new Date().toISOString().split("T")[0];
      const fileName = `LocationHistory_${empNameSafe}_${trackDate}.xlsx`;

      XLSX.writeFile(wb, fileName);
    } catch (err) {
      console.error("Export failed", err);
      alert(
        "Export to Excel failed: " +
          (err && err.message ? err.message : "unknown error"),
      );
    }
  };

  const handleReturn = () => navigate("/some-return-path");

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="bg-white p-4 rounded shadow text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {employeeInfo && (
        <div className="mb-4 bg-white p-4 rounded shadow">
          <h1 className="text-xl font-bold">{employeeInfo.name}</h1>
          <p className="text-sm text-gray-600">
            {employeeInfo.designation} - {employeeInfo.headquarter}
          </p>
        </div>
      )}

      <div ref={printableRef} className="border rounded shadow  table-wrapper">
        <table className="w-full text-xs border border-gray-300 bg-white">
          <thead className="bg-blue-600 text-white sticky top-0 z-10 shadow-sm text-center">
            <tr>
              <th className="border px-2 py-3">S.No</th>
              <th className="border px-2 py-3">Device ID</th>
              <th className="border px-2 py-3">Mobile</th>
              <th className="border px-2 py-3">Mobile Time</th>
              <th className="border px-2 py-3">Server Time</th>
              <th className="border px-2 py-3">I.P.</th>
              <th className="border px-2 py-3">Latitude</th>
              <th className="border px-2 py-3">Longitude</th>
              <th className="border px-2 py-3">Distance (K.M.)</th>
              <th className="border px-2 py-3">K.M. Covered</th>
              <th className="border px-2 py-3">Map</th>
              <th className="border px-2 py-3">Address</th>
              <th className="border px-2 py-3">Accuracy</th>
              <th className="border px-2 py-3">Data</th>
              <th className="border px-2 py-3">Type</th>
              <th className="border px-2 py-3">Provider</th>
              <th className="border px-2 py-3">Speed</th>
              <th className="border px-2 py-3 text-center">
                <div>Delete</div>
                <button
                  onClick={handleDeleteSelected}
                  disabled={selectedRows.size === 0}
                  className="mt-1 text-xs text-red-600 hover:underline"
                >
                  Delete Selected
                </button>
              </th>
              <th className="border px-2 py-3 text-center">
                <div>Select All</div>
                <input
                  type="checkbox"
                  checked={selectedRows.size === data.length && data.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 mt-1"
                />
              </th>
            </tr>
          </thead>

          <tbody>
            {data.map((row) => (
              <tr key={row.locationId} className={getRowColor(row)}>
                <td className="border px-2 py-2 text-center">{row.sNo}</td>
                <td className="border px-2 py-2">{row.deviceId}</td>
                <td className="border px-2 py-2">{row.mobile}</td>
                <td
                  className="border px-2 py-2 cursor-pointer text-blue-600 underline"
                  onClick={() => handleViewMap(row)}
                >
                  {row.mobileTime}
                </td>
                <td className="border px-2 py-2">{row.serverTime}</td>
                <td className="border px-2 py-2">{row.ip}</td>
                <td className="border px-2 py-2 text-right">{row.latitude}</td>
                <td className="border px-2 py-2 text-right">{row.longitude}</td>
                <td className="border px-2 py-2 text-right">{row.distance}</td>
                <td className="border px-2 py-2 text-right">{row.kmCovered}</td>
                <td className="border px-2 py-2 text-center">
                  <button
                    onClick={() => handleViewMap(row)}
                    className="text-blue-600 hover:underline"
                  >
                    view
                  </button>
                </td>
                <td
                  className="border px-2 py-2 text-blue-600 max-w-xs cursor-pointer underline"
                  onClick={() => handleViewMap(row)}
                >
                  {row.address}
                </td>
                <td className="border px-2 py-2 text-center">{row.accuracy}</td>
                <td className="border px-2 py-2 text-center">{row.dataMock}</td>
                <td className="border px-2 py-2 text-center">
                  {formatActivityType(row.type)}
                </td>

                <td className="border px-2 py-2 text-center capitalize">
                  {row.provider}
                </td>
                <td className="border px-2 py-2 text-center">{row.speed}</td>
                <td className="border px-2 py-2 text-center">
                  <button
                    onClick={() => handleDelete(row.locationId)}
                    className="bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded text-xs font-medium"
                  >
                    Remove
                  </button>
                </td>
                <td className="border px-2 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={selectedRows.has(row.locationId)}
                    onChange={() => handleSelectRow(row.locationId)}
                    className="w-4 h-4"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex gap-4 justify-center">
        <CustomButton onClick={handlePrint} label="Print" variant="print">
          🖨
        </CustomButton>
        <CustomButton
          onClick={handleExportExcel}
          label="Export"
          variant="export"
        >
          📥
        </CustomButton>
      </div>

      {mapView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60">
          <div className="bg-white rounded-lg w-full max-w-5xl h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-3 border-b">
              <h3 className="font-semibold">
                Employee Map - {mapView.employeeName}
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700 text-3xl font-sm bg-gray-100 hover:bg-gray-200 w-10 h-9 rounded-full flex items-center justify-center"
                onClick={() => setMapView(null)}
              >
                x
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <EmployeeMapTracker
                latitude={mapView.latitude}
                longitude={mapView.longitude}
                employeeId={mapView.employeeId}
                trackDate={mapView.trackDate}
                locationId={mapView.locationId}
                employeeName={mapView.employeeName}
                onClose={() => setMapView(null)}
                isModal={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LocationHistoryTime;
